import React from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { customSortWithNull } from 'components/common/Tables/Tables.utils';

import ActivityCell from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/Cells/activityCell';
import { sortFloat } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/utils';

export const initialState: ITableColumnState = {
    sortBy: [
        {
            id: 'criticalityScore',
            desc: true,
        },
        {
            id: 'currentStartDate',
        },
    ],
    hiddenColumns: [],
};

const narrowCols = [
    'actualduration',
    'criticalpath',
    'delaydriver',
    'projectweekfinish',
    'projectweekstart',
    'projectweekend',
    'projectmonthstart',
    'projectmonthfinish',
    'projectmonthend',
    'criticalityscore',
    'remainingduration',
    'float',
    'lag',
    'durationoverun',
    'latefinish',
    'latestart',
    'plannedduration',
    'completedduration',
    'forecastduration',
    'forecastoverrun',
    'overrundays',
    'overrunpercent',
    'type',
];
const smallCols = [
    'startdate',
    'finishdate',
    'numofpredecessors',
    'numofsuccessors',
    'projectname',
    'contractname',
    'activitystatus',
];
const mediumCols = [
    'plannedstartdate',
    'plannedfinishdate',
    'actualstartdate',
    'currentstartdate',
    'currentfinishdate',
    'actualfinishdate',
    'activitystatus',
];
const largeCols = ['activitytype', 'activityid', 'versionname', 'constrainttype'];
// humongous cols
const extraLargeCols = ['activityname', 'grandparentname', 'parentname', 'resourcename'];
const tooltipCells = [
    'activityId',
    'activityName',
    'activityType',
    'activityCode',
    'parentName',
    'grandParentName',
    'successorActivityName',
    'successorActivityId',
];
const isDateColumns = [
    'plannedStartDate',
    'plannedFinishDate',
    'actualStartDate',
    'actualFinishDate',
    'currentStartDate',
    'currentFinishDate',
    'finishDate',
    'startDate',
    'activityStartDate',
    'activityEndDate',
    'successorActivityStartDate',
    'successorActivityEndDate',
    'constraintTime',
    'baselineStart',
    'baselineFinish',
];

export const getColumns = ({ allColumns }) => {
    return allColumns
        .map((oneCol) => {
            // not all properties in a validation should map to a column
            const notValidColumns = ['activityDrilldown', 'delayDriver'];
            if (notValidColumns.includes(oneCol.accessor)) {
                return null;
            } else {
                const generatedColumn = {
                    ...oneCol,
                    Header: oneCol.header,
                    Filter: false,
                    id: oneCol.accessor,
                };

                const preppedAccessor = generatedColumn.accessor.toLowerCase().trim();
                if (narrowCols.includes(preppedAccessor)) {
                    generatedColumn.width = 90;
                } else if (smallCols.includes(preppedAccessor)) {
                    generatedColumn.width = 130;
                } else if (mediumCols.includes(preppedAccessor)) {
                    generatedColumn.width = 190;
                } else if (largeCols.includes(preppedAccessor)) {
                    generatedColumn.width = 220;
                } else if (extraLargeCols.includes(preppedAccessor)) {
                    generatedColumn.width = 370;
                } else {
                    generatedColumn.width = 190;
                }

                const cleanedHeader = oneCol.header.trim().toLowerCase();
                if (!generatedColumn.Cell) {
                    if (tooltipCells.includes(generatedColumn.accessor)) {
                        generatedColumn.Cell = ({ value }) => <TooltipCellWithCopy text={value} />;
                    } else {
                        generatedColumn.Cell = ({ value }) => {
                            return isDateColumns.includes(generatedColumn.accessor) && value
                                ? moment(value).format(constants.formats.date.default)
                                : value;
                        };
                    }

                    if (cleanedHeader === 'critical path') {
                        generatedColumn.id = 'criticalPath';
                        generatedColumn.accessor = (row) => (row.criticalPath ? 'Yes' : 'No');
                    }

                    if (cleanedHeader === 'criticality score') {
                        generatedColumn.sortType = customSortWithNull;
                    }

                    if (generatedColumn.accessor === 'lateStart') {
                        generatedColumn.sortType = customSortWithNull;
                    }

                    if (generatedColumn.accessor === 'lateFinish') {
                        generatedColumn.sortType = customSortWithNull;
                    }

                    if (cleanedHeader === 'activity name') {
                        generatedColumn.Cell = ({ value, row, toggleAllRowsSelected }) => {
                            const { toggleRowSelected } = row;
                            return (
                                <ActivityCell
                                    id={row.original.taskId}
                                    value={value}
                                    toggleRowSelected={toggleRowSelected}
                                    toggleAllRowsSelected={toggleAllRowsSelected}
                                />
                            );
                        };
                    }
                    if (cleanedHeader === 'float') {
                        generatedColumn.sortType = sortFloat;
                    }
                }
                return generatedColumn;
            }
        })
        .filter((col) => (col ? col : null));
};
